import React from 'react';
import { AlertTriangle, X, UserMinus, CalendarOff, ClockAlert, Loader2 } from 'lucide-react';

/**
 * ModalImpacto — pre-confirmación cuando un PUT a /campanas/:id va a tener
 * consecuencias (citas canceladas, personas removidas con citas activas, etc.).
 *
 * El backend devuelve el dossier vía POST /preview-impacto. Si tiene_impacto=false,
 * NO se muestra este modal y el guardado va directo.
 */
export default function ModalImpacto({
  abierto,
  impacto,
  guardando,
  onCancelar,
  onConfirmar,
}) {
  if (!abierto || !impacto) return null;

  const {
    tecnicos_eliminados = [],
    usuarios_eliminados = [],
    citas_fuera_de_rango = [],
    citas_fuera_de_horario = [],
    total_citas_a_cancelar = 0,
  } = impacto;

  const usuariosConCita = usuarios_eliminados.filter((u) => u.tiene_cita);
  const usuariosSinCita = usuarios_eliminados.filter((u) => !u.tiene_cita);

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4 py-8 overflow-y-auto"
      onClick={guardando ? undefined : onCancelar}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-2xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Confirmar cambios en la campaña
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Esta operación afectará a datos ya creados. Revísala antes de continuar.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancelar}
            disabled={guardando}
            className="w-7 h-7 grid place-items-center rounded-md hover:bg-slate-100 disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {tecnicos_eliminados.length > 0 && (
            <BloqueImpacto
              icon={<UserMinus className="w-4 h-4 text-red-500" />}
              titulo={`Técnicos que se quitan de la campaña (${tecnicos_eliminados.length})`}
            >
              <ul className="space-y-1">
                {tecnicos_eliminados.map((t) => (
                  <li key={t.id} className="text-xs text-slate-600 flex justify-between">
                    <span>{t.nombre || `Técnico #${t.id}`}</span>
                    {t.citas_afectadas > 0 && (
                      <span className="text-red-600">
                        {t.citas_afectadas} cita{t.citas_afectadas !== 1 ? 's' : ''} reservada{t.citas_afectadas !== 1 ? 's' : ''} se cancelará{t.citas_afectadas !== 1 ? 'n' : ''}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </BloqueImpacto>
          )}

          {usuariosConCita.length > 0 && (
            <BloqueImpacto
              icon={<UserMinus className="w-4 h-4 text-red-500" />}
              titulo={`Usuarios con cita que se quitan (${usuariosConCita.length})`}
              tono="rojo"
            >
              <p className="text-xs text-slate-500 mb-2">
                Sus citas reservadas se cancelarán automáticamente:
              </p>
              <ul className="space-y-0.5">
                {usuariosConCita.map((u) => (
                  <li key={u.id} className="text-xs text-slate-700">
                    {u.nombre || `Usuario #${u.id}`}
                  </li>
                ))}
              </ul>
            </BloqueImpacto>
          )}

          {usuariosSinCita.length > 0 && (
            <BloqueImpacto
              icon={<UserMinus className="w-4 h-4 text-slate-400" />}
              titulo={`Usuarios sin cita que se quitan (${usuariosSinCita.length})`}
              tono="neutro"
            >
              <p className="text-xs text-slate-500">
                No tienen citas reservadas — se quitan sin más.
              </p>
            </BloqueImpacto>
          )}

          {citas_fuera_de_rango.length > 0 && (
            <BloqueImpacto
              icon={<CalendarOff className="w-4 h-4 text-red-500" />}
              titulo={`Citas fuera del nuevo rango de fechas (${citas_fuera_de_rango.length})`}
              tono="rojo"
            >
              <ul className="text-xs text-slate-600 space-y-0.5 max-h-32 overflow-y-auto">
                {citas_fuera_de_rango.slice(0, 10).map((c) => (
                  <li key={c.id}>
                    {c.fecha} · {c.hora_inicio}
                  </li>
                ))}
                {citas_fuera_de_rango.length > 10 && (
                  <li className="text-slate-400 italic">
                    …y {citas_fuera_de_rango.length - 10} más
                  </li>
                )}
              </ul>
            </BloqueImpacto>
          )}

          {citas_fuera_de_horario.length > 0 && (
            <BloqueImpacto
              icon={<ClockAlert className="w-4 h-4 text-red-500" />}
              titulo={`Citas fuera del nuevo horario/duración (${citas_fuera_de_horario.length})`}
              tono="rojo"
            >
              <ul className="text-xs text-slate-600 space-y-0.5 max-h-32 overflow-y-auto">
                {citas_fuera_de_horario.slice(0, 10).map((c) => (
                  <li key={c.id}>
                    {c.fecha} · {c.hora_inicio}–{c.hora_fin}
                  </li>
                ))}
                {citas_fuera_de_horario.length > 10 && (
                  <li className="text-slate-400 italic">
                    …y {citas_fuera_de_horario.length - 10} más
                  </li>
                )}
              </ul>
            </BloqueImpacto>
          )}

          {total_citas_a_cancelar > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <div className="text-sm bg-red-50 border border-red-100 rounded-md p-3 text-red-800">
                <strong>Total de citas que se cancelarán: {total_citas_a_cancelar}</strong>
                <div className="text-xs text-red-700 mt-1">
                  Las citas pasarán a estado <em>cancelada por sistema</em>. La notificación
                  a usuarios y técnicos se enviará en una fase posterior.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50">
          <button
            type="button"
            onClick={onCancelar}
            disabled={guardando}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            disabled={guardando}
            className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center gap-1.5"
          >
            {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Aplicar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

function BloqueImpacto({ icon, titulo, tono = 'amarillo', children }) {
  const borde =
    tono === 'rojo' ? 'border-red-100 bg-red-50'
    : tono === 'neutro' ? 'border-slate-100 bg-slate-50'
    : 'border-amber-100 bg-amber-50';
  return (
    <div className={`border rounded-md ${borde} p-3`}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-slate-800 mb-2">
        {icon}
        {titulo}
      </div>
      {children}
    </div>
  );
}
